import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, User } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const StaffLogin = ({ role, title, description, redirectTo }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { loginWithStaff } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      toast.error('Please enter username and password');
      return;
    }
    
    setLoading(true);

    try {
      const response = await api.post('/staff/login', {
        username: username.trim(),
        password: password.trim()
      });
      
      const { staff, token } = response.data;

      // Check role permissions
      const allowedRoles = {
        pos: ['admin', 'pos', 'manager'],
        kitchen: ['admin', 'kitchen', 'manager'],
        admin: ['admin', 'manager'] // Manager can access admin (but with location restrictions)
      };

      if (!allowedRoles[role]?.includes(staff.role)) {
        toast.error(`You don't have access to this system.`);
        setLoading(false);
        return;
      }

      // Use AuthContext to save staff data (updates React state)
      loginWithStaff(staff, token);

      toast.success(`Welcome, ${staff.full_name || staff.username}!`);
      
      // Navigate after a short delay to ensure state is updated
      setTimeout(() => {
        navigate(redirectTo);
      }, 100);
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <Card className="bg-[#121212] border-white/10 w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF4500] flex items-center justify-center">
            <Lock className="w-8 h-8 text-white" strokeWidth={2} />
          </div>
          <CardTitle className="text-3xl text-white" style={{ fontFamily: 'Bebas Neue' }}>
            {title}
          </CardTitle>
          <CardDescription className="text-gray-400">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-white mb-2 block">
                Username
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="bg-white/5 border-white/10 text-white pl-10"
                  placeholder="Enter username"
                  autoFocus
                  data-testid="staff-username-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-white mb-2 block">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-white/5 border-white/10 text-white pl-10 pr-10"
                  placeholder="Enter password"
                  data-testid="staff-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading || !username || !password}
              className="w-full bg-[#FF4500] hover:bg-[#FF5722] text-white py-6 text-lg font-bold rounded-full"
              data-testid="staff-login-submit"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                ← Back to Menu
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffLogin;
